import { type ReactNode } from "react";
import dayjs from "dayjs";
import { Icon } from "@sellar-io/components";
import type { UnifiedCheckoutDeliveryGroup } from "../hooks/useUnifiedCheckoutSuccessDetails";

export const UnifiedCheckoutNextSteps = ({
  deliveryDates,
  deliveryGroups,
  deliveryPreference,
}: {
  deliveryDates: string[];
  deliveryGroups: UnifiedCheckoutDeliveryGroup[];
  deliveryPreference: "split" | "consolidated" | null;
}) => (
  <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
    <div className="border-b border-zinc-200 p-4">
      <h2 className="flex items-center gap-2 font-semibold">
        <Icon
          icon="HELP_CIRCLE_OUTLINE"
          size={0.75}
          className="text-purple-700"
        />
        What happens next
      </h2>
    </div>
    <div className="flex flex-col gap-4 p-4">
      <ol className="mb-0 flex flex-col text-sm">
        <NextStep number={1} title="Suppliers finalise your orders">
          You'll get an email to let you know when each of your suppliers have
          reviewed and finalised your orders.
        </NextStep>
        <NextStep number={2} title="Sellar's dray team picks everything up">
          Our drivers collect directly from each supplier on your delivery day.
          They're specialist drinks handlers so your stock is handled correctly
          from the moment it leaves the supplier.
        </NextStep>
        <NextStep number={3} title="Your delivery arrives">
          {deliveryGroups.flatMap((g) => g.suppliers).length === 1
            ? `Your order from ${deliveryGroups.flatMap((g) => g.suppliers)[0]} `
            : `Your orders from ${deliveryGroups.flatMap((g) => g.suppliers).join(", ")} `}
          {deliveryPreference === "split" ? (
            <>
              will arrive on{" "}
              <span className="font-semibold">
                {deliveryDates
                  .map((date) => dayjs(date).format("dddd, D MMM"))
                  .join(", ")}
              </span>
            </>
          ) : (
            <>
              {deliveryGroups.flatMap((g) => g.suppliers).length > 1
                ? "all arrive together on "
                : "will arrive on "}
              <span className="font-semibold">
                {dayjs(deliveryDates[0]).format("dddd, D MMM")}
              </span>
            </>
          )}
        </NextStep>
      </ol>
      <div className="flex gap-3 rounded-lg border border-zinc-300 bg-zinc-100 p-4">
        <Icon icon="COMMENT_TEXT_OUTLINE" size={0.8} />
        <p>
          <strong className="font-semibold">
            Got a question about an order?
          </strong>
          <br />
          Use order comments to message your supplier directly - it's the
          quickest way to get an answer and keeps the conversation in the right
          place
        </p>
      </div>
    </div>
  </section>
);

const NextStep = ({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) => (
  <li className="group flex gap-3 last:[&_.timeline-line]:hidden">
    <div className="flex w-6 shrink-0 flex-col items-center">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#8C23C7] text-xs font-semibold text-white">
        {number}
      </span>
      <span className="timeline-line my-1 min-h-5 w-px flex-1 bg-[#f3f4f6]" />
    </div>
    <div>
      <p className="text-base font-medium">{title}</p>
      <p className="pb-4 text-sm text-zinc-600">{children}</p>
    </div>
  </li>
);
